/**
 * CourseHome — /agentic-interviewing
 *
 * Public landing page for the Agentic AI Interviewing course.
 * Level 1 is accessible to any free account holder.
 * Levels 2–4 require an All Access subscription.
 *
 * Sections:
 *   1. Hero — headline, audience callout, dual CTAs
 *   2. What You'll Learn — 3 outcome pillars
 *   3. The Four Levels — overview cards with lock states
 *   4. How It Works — 3-step process
 *   5. Who This Is For — 3 persona cards
 *   6. Footer CTA banner
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { TFunction } from "i18next";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCourseProgress } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { PRICING, fmtUSD } from "../constants/pricing";
import { Trans, useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------

// LEVEL_META is a factory so color fields can reference MUI theme tokens.
// Call getLevelMeta(theme) inside components that have a theme reference.
function getLevelMeta(theme: Theme, t: TFunction) {
  return [
    {
      level: 1,
      title: t("CourseHome.level1Title"),
      description: t("CourseHome.level1Description"),
      questions: t("CourseHome.levelQuestionsCount"),
      color: theme.palette.success.main,
      free: true,
    },
    {
      level: 2,
      title: t("CourseHome.level2Title"),
      description: t("CourseHome.level2Description"),
      questions: t("CourseHome.levelQuestionsCount"),
      color: theme.palette.info.main,
      free: false,
    },
    {
      level: 3,
      title: t("CourseHome.level3Title"),
      description: t("CourseHome.level3Description"),
      questions: t("CourseHome.levelQuestionsCount"),
      color: theme.palette.primary.main,
      free: false,
    },
    {
      level: 4,
      title: t("CourseHome.level4Title"),
      description: t("CourseHome.level4Description"),
      questions: t("CourseHome.levelQuestionsCount"),
      color: theme.palette.warning.main,
      free: false,
    },
  ];
}

const WHAT_YOU_LEARN = [
  {
    icon: "🎯",
    titleKey: "CourseHome.learnStructuredTitle",
    bodyKey: "CourseHome.learnStructuredBody",
  },
  {
    icon: "🤖",
    titleKey: "CourseHome.learnAgenticTitle",
    bodyKey: "CourseHome.learnAgenticBody",
  },
  {
    icon: "📈",
    titleKey: "CourseHome.learnFeedbackTitle",
    bodyKey: "CourseHome.learnFeedbackBody",
  },
];

const PERSONAS = [
  {
    icon: "🔀",
    titleKey: "CourseHome.personaSwitcherTitle",
    bodyKey: "CourseHome.personaSwitcherBody",
  },
  {
    icon: "⚡",
    titleKey: "CourseHome.personaEngineerTitle",
    bodyKey: "CourseHome.personaEngineerBody",
  },
  {
    icon: "📋",
    titleKey: "CourseHome.personaPmTitle",
    bodyKey: "CourseHome.personaPmBody",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    titleKey: "CourseHome.step01Title",
    bodyKey: "CourseHome.step01Body",
  },
  {
    step: "02",
    titleKey: "CourseHome.step02Title",
    bodyKey: "CourseHome.step02Body",
  },
  {
    step: "03",
    titleKey: "CourseHome.step03Title",
    bodyKey: "CourseHome.step03Body",
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LevelCard({
  meta,
  completed,
  locked,
  practicesCount,
  hasAccount,
}: {
  meta: ReturnType<typeof getLevelMeta>[number];
  completed: boolean;
  locked: boolean;
  practicesCount: number;
  hasAccount: boolean;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-core");

  const handleClick = () => {
    if (!locked) {
      navigate(`/agentic-interviewing/level/${meta.level}`);
    }
  };

  return (
    <Paper
      onClick={locked ? undefined : handleClick}
      elevation={0}
      sx={{
        border: `1px solid ${locked ? theme.palette.divider : alpha(meta.color, 0.3)}`,
        borderRadius: 3,
        p: 3,
        position: "relative",
        cursor: locked ? "not-allowed" : "pointer",
        opacity: locked ? 0.6 : 1,
        transition: "all 0.2s ease",
        "&:hover": locked
          ? {}
          : {
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[4],
              borderColor: meta.color,
            },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          borderRadius: "12px 12px 0 0",
          background: meta.color,
          opacity: locked ? 0.3 : 1,
        },
      }}
    >
      {/* Level number + lock/check */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Chip
          label={t("CourseHome.levelChipLabel", { level: meta.level })}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.7rem",
            letterSpacing: "0.06em",
            bgcolor: alpha(meta.color, 0.1),
            color: meta.color,
          }}
        />
        {completed ? (
          <CheckCircleIcon sx={{ color: meta.color, fontSize: 22 }} />
        ) : locked ? (
          <LockIcon sx={{ color: "text.disabled", fontSize: 20 }} />
        ) : (
          <LockOpenIcon sx={{ color: meta.color, fontSize: 20 }} />
        )}
      </Stack>

      {/* Title + description */}
      <Typography variant="subtitle1" fontWeight={500} gutterBottom>
        {meta.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        {meta.description}
      </Typography>

      {/* Progress bar (logged-in users only) */}
      {hasAccount && !locked && !completed && (
        <Box mb={2}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t("CourseHome.practiceProgressLabel")}
            </Typography>
            <Typography variant="caption" fontWeight={500} sx={{ color: meta.color }}>
              {Math.min(practicesCount, 3)} / 3
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min((practicesCount / 3) * 100, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(meta.color, 0.15),
              "& .MuiLinearProgress-bar": { bgcolor: meta.color, borderRadius: 3 },
            }}
          />
        </Box>
      )}

      {/* Free badge or lock message */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {meta.free ? (
          <Chip
            label={t("CourseHome.freeChipLabel")}
            size="small"
            sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, fontWeight: 500, fontSize: "0.7rem" }}
          />
        ) : locked ? (
          <Typography variant="caption" color="text.disabled">
            {t("CourseHome.allAccessRequiredLabel")}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {meta.questions}
          </Typography>
        )}
        {!locked && (
          <ArrowForwardIcon sx={{ fontSize: 16, color: meta.color }} />
        )}
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CourseHome() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const LEVEL_META = getLevelMeta(theme, t);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: progressData } = useQuery({
    queryKey: ["courseProgress"],
    queryFn: getCourseProgress,
    enabled: !!user,
  });

  const progressMap = Object.fromEntries(
    (progressData?.levels ?? []).map((l: { level: number; practices_count: number; completed: boolean; locked: boolean }) => [
      l.level,
      l,
    ])
  );

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.info.main} 100%)`,
          color: "white",
          py: { xs: 6, md: 8 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* subtle background glow */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 80%, rgba(56,161,105,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66,153,225,0.15) 0%, transparent 50%)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <Chip
            label={t("CourseHome.allAccessPriceChip", { price: fmtUSD(PRICING.allAccess.monthly) })}
            sx={{
              mb: 3,
              bgcolor: alpha(theme.palette.success.main, 0.2),
              border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
              color: theme.palette.success.light,
              fontWeight: 500,
              fontSize: "0.78rem",
              letterSpacing: "0.04em",
            }}
          />
          <Typography
            variant="h2"
            fontWeight={500}
            sx={{
              fontSize: { xs: "2.2rem", md: "3.2rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            <Trans
              i18nKey="CourseHome.heroTitle"
              t={t}
              components={[
                <Box key="span0" component="span" sx={{ color: theme.palette.secondary.light }} />,
              ]}
            />
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "rgba(255,255,255,0.82)", maxWidth: 600, mx: "auto", mb: 4, fontWeight: 400, lineHeight: 1.6 }}
          >
            {t("CourseHome.heroSubtitle")}
          </Typography>

          {/* Audience chips */}
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" mb={5}>
            {["audienceEngineers", "audiencePmsTpms", "audienceCareerSwitchers"].map((key) => (
              <Chip
                key={key}
                label={t(`CourseHome.${key}`)}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 500,
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              />
            ))}
          </Stack>

          {/* CTAs */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/agentic-interviewing/level/1")}
              sx={{
                bgcolor: theme.palette.secondary.main,
                "&:hover": { bgcolor: theme.palette.secondary.light },
                px: 4,
                py: 1.5,
                fontWeight: 500,
                fontSize: "1rem",
              }}
            >
              {t("CourseHome.startLevel1FreeButton")}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/pricing")}
              sx={{
                borderColor: "rgba(255,255,255,0.5)",
                color: "white",
                "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.08)" },
                px: 4,
                py: 1.5,
                fontWeight: 500,
              }}
            >
              {t("CourseHome.getAllAccessButton")}
            </Button>
          </Stack>

          {/* Stats */}
          <Stack
            direction="row"
            spacing={{ xs: 3, md: 6 }}
            justifyContent="center"
            mt={6}
            flexWrap="wrap"
          >
            {[
              { value: "4", labelKey: "CourseHome.statCourseLevels" },
              { value: "40+", labelKey: "CourseHome.statPracticeQuestions" },
              { value: "6", labelKey: "CourseHome.statScoringDimensions" },
              { value: "AI", labelKey: "CourseHome.statPoweredByClaude" },
            ].map(({ value, labelKey }) => (
              <Box key={labelKey} textAlign="center">
                <Typography variant="h4" fontWeight={500} sx={{ color: theme.palette.secondary.light }}>
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {t(labelKey)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── What You'll Learn ─────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Typography variant="h4" fontWeight={500} textAlign="center" gutterBottom>
          {t("CourseHome.whatYoullLearnHeading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4, maxWidth: 520, mx: "auto" }}>
          {t("CourseHome.whatYoullLearnSubheading")}
        </Typography>
        <Grid container spacing={3}>
          {WHAT_YOU_LEARN.map(({ icon, titleKey, bodyKey }) => (
            <Grid item xs={12} sm={4} key={titleKey}>
              <Paper elevation={0} sx={{ p: 3, height: "100%", border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                <Typography fontSize="2rem" mb={1.5}>{icon}</Typography>
                <Typography variant="subtitle1" fontWeight={500} gutterBottom>{t(titleKey)}</Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{t(bodyKey)}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* ── The Four Levels ───────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Typography variant="h4" fontWeight={500} textAlign="center" gutterBottom>
          {t("CourseHome.fourLevelsHeading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4, maxWidth: 520, mx: "auto" }}>
          {t("CourseHome.fourLevelsSubheading")}
        </Typography>
        <Grid container spacing={2.5}>
          {LEVEL_META.map((meta) => {
            const progress = progressMap[meta.level];
            return (
              <Grid item xs={12} sm={6} key={meta.level}>
                <LevelCard
                  meta={meta}
                  completed={progress?.completed ?? false}
                  locked={progress?.locked ?? meta.level > 1}
                  practicesCount={progress?.practices_count ?? 0}
                  hasAccount={!!user}
                />
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <Divider />

      {/* ── How It Works ──────────────────────────────────────── */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 4, md: 7 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={500} textAlign="center" gutterBottom>
            {t("CourseHome.howItWorksHeading")}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4, maxWidth: 480, mx: "auto" }}>
            {t("CourseHome.howItWorksSubheading")}
          </Typography>
          <Stack spacing={4}>
            {HOW_IT_WORKS.map(({ step, titleKey, bodyKey }) => (
              <Stack key={step} direction="row" spacing={3} alignItems="flex-start">
                <Box
                  sx={{
                    minWidth: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: theme.palette.primary.main,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 500,
                    fontSize: "0.85rem",
                    flexShrink: 0,
                  }}
                >
                  {step}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={500} gutterBottom>{t(titleKey)}</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{t(bodyKey)}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>

      <Divider />

      {/* ── Who This Is For ───────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Typography variant="h4" fontWeight={500} textAlign="center" gutterBottom>
          {t("CourseHome.whoThisIsForHeading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4, maxWidth: 480, mx: "auto" }}>
          {t("CourseHome.whoThisIsForSubheading")}
        </Typography>
        <Grid container spacing={3}>
          {PERSONAS.map(({ icon, titleKey, bodyKey }) => (
            <Grid item xs={12} sm={4} key={titleKey}>
              <Paper elevation={0} sx={{ p: 3, height: "100%", border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                <Typography fontSize="2rem" mb={1.5}>{icon}</Typography>
                <Typography variant="subtitle1" fontWeight={500} gutterBottom>{t(titleKey)}</Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{t(bodyKey)}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Footer CTA ────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          py: { xs: 6, md: 8 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="sm">
          <EmojiEventsIcon sx={{ fontSize: 48, color: theme.palette.secondary.light, mb: 2 }} />
          <Typography variant="h4" fontWeight={500} color="white" gutterBottom>
            {t("CourseHome.footerHeading")}
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)", mb: 4 }}>
            {t("CourseHome.footerBody")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/agentic-interviewing/level/1")}
            sx={{
              bgcolor: theme.palette.secondary.main,
              "&:hover": { bgcolor: theme.palette.secondary.light },
              px: 5,
              py: 1.5,
              fontWeight: 500,
              fontSize: "1rem",
            }}
          >
            {t("CourseHome.startForFreeButton")}
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
